import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { ItemComponentTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./ItemComponentTypesPage.scss";

interface ItemComponentTypesPageProps {
    itemComponentTypes: Dtos.ItemComponentType[];
    formFilter: IFormFilter;
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
    filter: ItemComponentTypeActions.IItemComponentTypeFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const ItemComponentTypesPage: FC<ItemComponentTypesPageProps> = ({
    itemComponentTypes,
    formFilter,
    loadingItemComponentTypes,
    loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    permissions,
    pageSize,
    clearItemComponentTypes,
    loadItemComponentTypes,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showItemComponentTypes =
        !loadingItemComponentTypes && loadItemComponentTypesSuccess;


    // Load Item Component Types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(ItemComponentTypesBreadcrumbs());

            loadItemComponentTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        ItemComponentTypesBreadcrumbs,
        loadItemComponentTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the Item Component Types on component unmount
    useEffect(() => {
        return () => {
            clearItemComponentTypes();
        };
    }, [clearItemComponentTypes]);

    const createTable = useMemo(() => {
        if (!itemComponentTypes || itemComponentTypes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no ItemComponent Types to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = itemComponentTypes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={itemComponentTypes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : itemComponentTypes.length
                    }
                    pageSize={
                        showPagination ? pageSize : itemComponentTypes.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no Item Component Types to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/general/itemcomponenttypes/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        itemComponentTypes,
        PAGE_SIZE_OPTIONS,
        pageSize,
        setPageSize,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Item Component Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ItemComponentTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/general/itemcomponenttypes/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showItemComponentTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showItemComponentTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showItemComponentTypes, createTable]);

    return (
        <LookupsLayout
            loading={loadingItemComponentTypes}
            permission={Dtos.Permission.ItemComponentTypesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ItemComponentTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        itemComponentTypes: state.itemComponentTypes.formFilteredData!,
        formFilter: state.itemComponentTypes.formFilter,
        loadingItemComponentTypes:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode:
            state.itemComponentTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<ItemComponentTypesPageProps> => {
    return {
        loadItemComponentTypes: bindActionCreators(
            ItemComponentTypeActions.LoadItemComponentType,
            dispatch
        ),
        clearItemComponentTypes: bindActionCreators(
            ItemComponentTypeActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(ItemComponentTypeActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ItemComponentTypesPage);
